import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import Grid from '@mui/material/Grid';

import { buttonVariants, Dialog } from '@clatter/ui';
import { formatDate, useDeepCompareEffect, useDeepCompareMemo } from '@clatter/platform';
import { config } from '../../../../config';
import { addNotification } from '../../../../store';
import { useUpdateDocumentMutation } from '../../../../services';
import {
  AccessList,
  FolderManagerDynamicForm,
  NgButton,
  NgCard,
  NgCardContent,
  NgCardTitle,
  NgErrorMessage,
} from '../../../../components';

import { TemplateLabel } from './TemplateLabel';
import { DocumentOutputTable } from './DocumentOutputTable';

//region ManagePermittedViewersDialog
interface ManagePermittedViewersDialogProps {
  item: MicrositeListItem;
  projectData: any;
  opened: boolean;
  onClose: () => void;
  onSubmit: (item: MicrositeListItem, data: { [key: string]: any }) => void;
  isLoading?: boolean;
}

const formSchema = {
  validation: {
    type: 'object',
    properties: {
      emailWhitelist: {
        type: 'string',
        pattern: '^\\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}|[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,})\\s*$',
        errorMessage: {
          pattern: 'Must be a valid email address or domain',
        },
      },
    },
    required: [],
  },
  fields: [
    {
      name: 'emailWhitelist',
      type: 'string',
      label: 'Email / Domain',
    },
  ],
};

const StyledContainer = styled.div`
  min-height: 30vh;

  .header {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};
  }

  #submit-btn-container {
    button {
      margin-bottom: 1.5rem;
    }
  }
`;

const ManagePermittedViewersDialog: FC<ManagePermittedViewersDialogProps> = ({
  opened,
  isLoading: isParentLoading,
  item,
  projectData,
  onClose,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [emailWhitelist, setEmailWhitelist] = useState<string[]>([]);

  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();

  const isLoading = isParentLoading || isUpdateDocumentLoading;

  const accessList = useDeepCompareMemo(() => {
    if (!emailWhitelist || !Array.isArray(emailWhitelist)) {
      return [];
    }

    return emailWhitelist.map((item: string, index: number) => ({
      ID: index,
      Name: item,
      Type: 'email',
    }));
  }, [emailWhitelist]);

  const handleFormAction = async (data: any) => {
    if (data === 'cancel') {
      return onClose();
    }

    if (errorMessage) {
      setErrorMessage(null);
    }

    const payload = {
      ...(item?.microsite || {}),
    };

    if (!emailWhitelist || !Array.isArray(emailWhitelist)) {
      console.error('Error updating access: No emailWhitelist data!');
      return;
    }

    // remove case
    if (data?.type === 'REVOKE') {
      payload['emailWhitelist'] = emailWhitelist.filter((item) => item !== data?.payload?.Name);
    }

    // submit form case
    if (data?.emailWhitelist) {
      if (emailWhitelist?.includes(data.emailWhitelist)) {
        setErrorMessage('Email or domain has already been added!');
        return;
      }
      payload['emailWhitelist'] = [...emailWhitelist, data.emailWhitelist];
    }

    try {
      await updateDocument({
        documentId: item.id,
        projectId: projectData.ID,
        data: { Microsite: JSON.stringify(payload) },
      }).unwrap();

      setEmailWhitelist(payload.emailWhitelist);
    } catch (error: any) {
      setErrorMessage('Failed to update the microsite.');
      console.error('Failed to update the microsite.');
    }
  };

  useDeepCompareEffect(() => {
    setEmailWhitelist(item?.microsite?.emailWhitelist || []);
  }, [item]);

  if (!opened) {
    return null;
  }

  return (
    <Dialog title="Manage Permitted Viewers" onCloseDialog={() => onClose()} open={opened} noBorder includeFooter>
      <StyledContainer>
        <div className="header">
          <FolderManagerDynamicForm
            formSchema={formSchema}
            initialValues={{ emailWhitelist: '' }}
            isLoading={isLoading || false}
            errors={errorMessage}
            onAction={handleFormAction}
            submitButtonLabel="Add"
            buttonsTargetContainerId="dialog-footer"
            includeCancelButton
            clearAfterSubmit
            hideResetButton
          />
        </div>

        <AccessList list={accessList} onAction={handleFormAction} />
      </StyledContainer>
    </Dialog>
  );
};

//endregion

interface MicrositesPublishSectionProps {
  projectData: any;
  isQueryValid: boolean;
  isQueriesEnabled: boolean;
  isLoading: boolean;
  attributes?: { [key: string]: string }[];
}

interface MicrositeListItem {
  id: number;
  name: string;
  type: string;
  previewLink?: string;
  lastUpdated: string;
  queryID?: number;
  microsite: {
    emailWhitelist: string[];
    published: boolean;
    publishedAt: string | null;
    protected: boolean;
  };
}

const StyledMicrositeLabelMeta = styled.div`
  display: flex;
  flex-direction: column;

  font-size: ${({ theme }) => theme.fontSize.default};
`;

const StyledPermissionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};

  input {
    cursor: pointer;
  }

  a {
    margin-top: 3px;

    &:hover {
      text-decoration: underline;
    }

    font-size: ${({ theme }) => theme.fontSize.default};
  }
`;

export const MicrositesPublishSection: FC<MicrositesPublishSectionProps> = ({
  projectData,
  isLoading: isParentLoading,
  isQueryValid,
  isQueriesEnabled,
}) => {
  const dispatch = useDispatch();

  const [manageViewersDialogState, setManageViewersDialogState] = useState<any>({ opened: false, item: null });

  const [selectedItem, setSelectedItem] = useState<MicrositeListItem | null>(null);

  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();

  const isLoading = isParentLoading || isUpdateDocumentLoading;

  // Note: This list should contain document templates, not instances.
  // It should follow the same pattern as the documents output selector—
  // users can select a template and click "Generate" to create microsite instances using the current workspace data.
  // Once generated, the microsites should appear under a left-rail menu labeled "Microsites",
  // which will allow users to manage the generated instances.
  const micrositesList: MicrositeListItem[] = useDeepCompareMemo(() => {
    return (projectData?.Documents || [])
      .map((item: any) => {
        if ((item?.DocumentTemplate?.AllowedOutputTypes || []).includes('html')) {
          const microsite = JSON.parse(item?.Microsite || '{}');

          return {
            id: item?.ID,
            name: item?.DocumentTemplate?.DisplayName || item?.DocumentTemplate?.OutputType || '',
            type: item?.DocumentTemplate?.OutputType,
            previewLink: `${config.assetServiceBaseUrl}/microsites/preview?slug=/${projectData?.ID}/${item?.ID}`,
            lastUpdated: item?.DocumentTemplate?.UpdatedAt,
            queryID: item?.DocumentTemplate?.Query?.ID,
            microsite: microsite,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [projectData]);

  const publishValidationState: { disabled: boolean; message: string | null } = useDeepCompareMemo(() => {
    //@todo - restrict also if no workbook has been uploaded (e.g there is no data)

    if (!isQueryValid) {
      return {
        disabled: true,
        message: isQueriesEnabled
          ? 'Please run a valid query before publishing microsites.'
          : 'Please complete all required fields in the Values form before publishing a microsite.',
      };
    }

    return {
      disabled: false,
      message: null,
    };
  }, [isQueryValid]);

  const handleManagePermittedViewersClick = (event: React.MouseEvent<HTMLElement>, item: MicrositeListItem) => {
    event.preventDefault();
    event.stopPropagation();

    setManageViewersDialogState({ opened: true, item: item });
  };

  const handleMicrositeUpdate = async (item: MicrositeListItem, data: { [key: string]: any }) => {
    let payload = {
      ...(item?.microsite || {}),
      ...data,
    };

    // should be handled in the api...
    if ('published' in data) {
      payload = {
        ...payload,
        publishedAt: data?.['published'] ? new Date().toISOString() : null,
      };
    }

    try {
      await updateDocument({
        documentId: item.id,
        projectId: projectData.ID,
        data: { Microsite: JSON.stringify(payload) },
      }).unwrap();

      if (manageViewersDialogState?.opened) {
        setManageViewersDialogState({ opened: false, item: null });
      }

      dispatch(
        addNotification({
          type: 'success',
          message: 'The microsite has been successfully updated.',
        }),
      );
    } catch (error: any) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Failed to update the microsite.',
        }),
      );
      console.error('Failed to update the microsite.');
    }
  };

  if (micrositesList?.length <= 0) {
    return null;
  }

  return (
    <>
      <NgCard>
        <NgCardContent>
          <NgCardTitle title={selectedItem ? 'Microsite Settings' : 'Microsites'} />
          <DocumentOutputTable>
            <thead>
              <tr>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {micrositesList.map((item) => {
                const { publishedAt, protected: isProtected, published, emailWhitelist } = item?.microsite || {};

                return (
                  <tr key={item.id}>
                    <td>
                    <Grid container sx={{ mb: 1 }}>
                      <Grid container size={12}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <TemplateLabel previewLink={item?.previewLink} name={item?.name}>
                              <StyledMicrositeLabelMeta>
                                <small>Date modified: {formatDate(item.lastUpdated)}</small>
                                {!!publishedAt && <small>Date published: {formatDate(new Date().toISOString())}</small>}
                              </StyledMicrositeLabelMeta>
                            </TemplateLabel>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }} sx={{ justifyItems: "flex-end", alignContent: "center"}}>
                            <NgButton
                              onClick={() => handleMicrositeUpdate(item, { published: !published })}
                              disabled={isLoading || (!published && publishValidationState?.disabled)}
                              variant={published ? buttonVariants.clear : buttonVariants.primary}
                            >
                              {published ? 'Unpublish' : 'Publish'}
                            </NgButton>
                          </Grid>
                        </Grid>

                        <Grid size={12}>
                          <StyledPermissionsContainer>
                            <div className="inputs">
                              <label style={{ marginRight: '12px' }}>
                                <input
                                  type="radio"
                                  name={`visibility-public-${item.id}`}
                                  value="Public"
                                  checked={!isProtected}
                                  disabled={isLoading}
                                  onChange={() => handleMicrositeUpdate(item, { protected: false })}
                                />
                                <span>Public</span>
                              </label>

                              <label>
                                <input
                                  type="radio"
                                  name={`visibility-protected-${item.id}`}
                                  value="Protected"
                                  checked={isProtected}
                                  disabled={isLoading}
                                  onChange={() => handleMicrositeUpdate(item, { protected: true })}
                                />
                                <span>Protected</span>
                              </label>
                            </div>

                            {isProtected && (
                              <a href="#" onClick={(e) => handleManagePermittedViewersClick(e, item)}>
                                Manage Permitted Viewers
                              </a>
                            )}
                          </StyledPermissionsContainer>
                        </Grid>
                      </Grid>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DocumentOutputTable>

          <br />

          <NgErrorMessage message={publishValidationState?.message} />
        </NgCardContent>
      </NgCard>

      <ManagePermittedViewersDialog
        isLoading={isLoading}
        projectData={projectData}
        item={manageViewersDialogState.item}
        opened={manageViewersDialogState.opened}
        onSubmit={handleMicrositeUpdate}
        onClose={() => setManageViewersDialogState({ opened: false, item: null })}
      />
    </>
  );
};
